<template>
    <BaseContainer>
        <span slot="principal">
            <v-content class="pr-2 pl-2">
                <v-toolbar class="ma-2" style="border-radius: 30px;">
                    <v-btn icon link to="/">
                        <v-icon>mdi-arrow-left</v-icon>
                    </v-btn>
                    <v-spacer/>
                    <v-toolbar-title v-show="$vuetify.breakpoint.smAndUp">
                        Leads que viraram cliente
                    </v-toolbar-title>
                    <v-spacer/>
                    <PeriodoFilter
                        v-model="periodo"
                        defaultPeriodo="Últimos 12 meses"
                        label="Entrada do lead"
                        @update="consultar"
                    />
                </v-toolbar>

                <v-alert v-if="!podeConsultar" type="info" text class="ma-1">
                    Você não tem permissão para consultar este relatório.
                    Peça a um administrador a permissão
                    <strong>Zapercontrol Relatorios Leads Convertidos Consultar</strong>
                    em Usuários ▸ Permissões.
                </v-alert>

                <template v-else>

                <v-card elevation="2" class="ma-1">
                <v-card-text>

                <v-alert
                    v-if="resumo.leads_outliers > 0"
                    :type="resumo.outliers_incluidos ? 'error' : 'warning'"
                    dense
                    text
                >
                    <div class="d-flex flex-wrap align-center">
                        <span>
                            {{ resumo.leads_outliers }}
                            {{ resumo.leads_outliers === 1 ? 'lead tem' : 'leads têm' }}
                            mais de {{ resumo.limite_empresas_por_lead }} empresas vinculadas —
                            sinal de uuid de lead reaproveitado no cadastro, não de cliente real.
                            <strong>{{ textoAlertaOutliers }}</strong>
                        </span>
                        <v-spacer/>
                        <v-switch
                            v-model="incluirOutliers"
                            label="Incluir mesmo assim"
                            hide-details
                            dense
                            class="mt-0 ml-4"
                            @change="consultar"
                        />
                    </div>
                </v-alert>

                <!-- Funil -->
                <v-row>
                    <v-col cols="12" sm="6" md="3">
                        <Card
                            :value="String(resumo.leads)"
                            label="Leads no período"
                            color="blue-grey"
                            icon="mdi-account-multiple"
                        />
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                        <Card
                            :value="`${resumo.convertidos} (${resumo.taxa_conversao}%)`"
                            label="Viraram empresa"
                            color="blue darken-1"
                            icon="mdi-store"
                        />
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                        <Card
                            :value="`${resumo.pagantes} (${resumo.taxa_pagante}%)`"
                            label="Viraram pagantes"
                            color="success"
                            icon="mdi-cash-check"
                        />
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                        <Card
                            :value="`R$ ${formatPrice(resumo.receita)}`"
                            label="Receita gerada"
                            color="green darken-2"
                            icon="mdi-cash-100"
                        />
                    </v-col>
                </v-row>

                <!-- Qualidade do lead -->
                <v-row>
                    <v-col cols="12" sm="6" md="3">
                        <Card
                            :value="String(resumo.score_medio_pagantes === null ? '—' : resumo.score_medio_pagantes)"
                            label="Score médio — pagantes"
                            color="deep-purple"
                            icon="mdi-star-circle"
                        />
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                        <Card
                            :value="String(resumo.score_medio_convertidos === null ? '—' : resumo.score_medio_convertidos)"
                            label="Score médio — convertidos"
                            color="indigo"
                            icon="mdi-star-half-full"
                        />
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                        <Card
                            :value="String(resumo.score_medio_nao_convertidos === null ? '—' : resumo.score_medio_nao_convertidos)"
                            label="Score médio — não convertidos"
                            color="grey darken-1"
                            icon="mdi-star-outline"
                        />
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                        <Card
                            :value="resumo.dias_ate_converter === null ? '—' : `${resumo.dias_ate_converter} dias`"
                            label="Do lead ao cadastro"
                            color="teal"
                            icon="mdi-clock-outline"
                        />
                    </v-col>
                </v-row>

                <!-- Quebra por característica do lead -->
                <v-card class="mx-auto" elevation="4" outlined>
                    <div class="text-overline subtitle-2 ma-2" style="text-align: center;">
                        Que tipo de lead vira cliente
                    </div>
                    <div class="d-flex justify-center mb-2">
                        <v-select
                            v-model="dimensao"
                            :items="dimensoes"
                            outlined
                            dense
                            hide-details
                            label="Agrupar por"
                            style="max-width: 260px;"
                            @change="consultarDimensao"
                        />
                    </div>
                    <v-card-text>
                        <v-data-table
                            :headers="headersDimensao"
                            :items="porDimensao"
                            :mobileBreakpoint="0"
                            :loading="loadingDimensao"
                            :items-per-page="-1"
                            hide-default-footer
                            dense
                        >
                            <template v-slot:item.taxa_conversao="{ item }">
                                {{ item.taxa_conversao }}%
                            </template>
                            <template v-slot:item.taxa_pagante="{ item }">
                                <v-chip :color="corTaxa(item.taxa_pagante)" x-small dark>
                                    {{ item.taxa_pagante }}%
                                </v-chip>
                            </template>
                            <template v-slot:item.receita="{ item }">
                                R$ {{ formatPrice(item.receita) }}
                            </template>
                        </v-data-table>
                    </v-card-text>
                </v-card>

                <!-- Listagem lead a lead -->
                <v-card class="mx-auto mt-4" elevation="4" outlined>
                    <div class="text-overline subtitle-2 ma-2" style="text-align: center;">
                        Leads
                        <v-btn icon small class="ml-1" @click="filtrosAbertos = !filtrosAbertos">
                            <v-icon>{{ filtrosAbertos ? 'mdi-filter-off' : 'mdi-filter' }}</v-icon>
                        </v-btn>
                    </div>

                    <v-expand-transition>
                        <!-- pt-4: o v-expand-transition corta o conteúdo, e sem folga
                             o label flutuante do primeiro campo fica pela metade. -->
                        <v-card-text v-show="filtrosAbertos" class="pt-4 pb-0">
                            <v-row dense>
                                <v-col cols="12" sm="4" md="3">
                                    <v-select
                                        v-model="filtros.situacao"
                                        :items="situacoes"
                                        outlined
                                        dense
                                        hide-details
                                        label="Situação"
                                    />
                                </v-col>
                                <v-col cols="6" sm="4" md="2">
                                    <v-text-field
                                        v-model.number="filtros.score_min"
                                        type="number"
                                        outlined
                                        dense
                                        hide-details
                                        label="Score mín."
                                    />
                                </v-col>
                                <v-col cols="6" sm="4" md="2">
                                    <v-text-field
                                        v-model.number="filtros.score_max"
                                        type="number"
                                        outlined
                                        dense
                                        hide-details
                                        label="Score máx."
                                    />
                                </v-col>
                                <v-col cols="12" sm="6" md="5">
                                    <v-select
                                        v-model="filtros.ramo"
                                        :items="opcoes.ramo"
                                        multiple
                                        chips
                                        deletable-chips
                                        outlined
                                        dense
                                        hide-details
                                        label="Ramo"
                                    />
                                </v-col>
                                <v-col cols="12" sm="6" md="4">
                                    <v-select
                                        v-model="filtros.faturamento"
                                        :items="opcoes.faturamento"
                                        multiple
                                        chips
                                        deletable-chips
                                        outlined
                                        dense
                                        hide-details
                                        label="Faturamento"
                                    />
                                </v-col>
                                <v-col cols="12" sm="6" md="4">
                                    <v-select
                                        v-model="filtros.tempo_empresa"
                                        :items="opcoes.tempo_empresa"
                                        multiple
                                        chips
                                        deletable-chips
                                        outlined
                                        dense
                                        hide-details
                                        label="Tempo de empresa"
                                    />
                                </v-col>
                                <v-col cols="12" sm="6" md="4">
                                    <v-select
                                        v-model="filtros.pedidos"
                                        :items="opcoes.pedidos"
                                        multiple
                                        chips
                                        deletable-chips
                                        outlined
                                        dense
                                        hide-details
                                        label="Pedidos/dia"
                                    />
                                </v-col>
                                <v-col cols="12" class="d-flex">
                                    <v-spacer/>
                                    <v-btn text small @click="limparFiltros">Limpar</v-btn>
                                    <v-btn color="primary" small class="ml-2" @click="aplicarFiltros">
                                        Aplicar
                                    </v-btn>
                                </v-col>
                            </v-row>
                        </v-card-text>
                    </v-expand-transition>

                    <v-card-text>
                        <v-data-table
                            :headers="headersLista"
                            :items="lista"
                            :mobileBreakpoint="0"
                            :loading="loadingLista"
                            :options.sync="options"
                            :items-per-page="-1"
                            hide-default-footer
                            dense
                            :server-items-length="paginacao.total"
                        >
                            <template v-slot:item.lead_score="{ item }">
                                <v-chip :color="corScore(item.lead_score)" x-small dark>
                                    {{ item.lead_score }}
                                </v-chip>
                            </template>
                            <template v-slot:item.ramo="{ item }">
                                {{ metadataValor(item, 'ramo') }}
                            </template>
                            <template v-slot:item.faturamento="{ item }">
                                {{ metadataValor(item, 'faturamento') }}
                            </template>
                            <template v-slot:item.tempo_empresa="{ item }">
                                {{ metadataValor(item, 'tempo_empresa') }}
                            </template>
                            <template v-slot:item.pedidos="{ item }">
                                {{ metadataValor(item, 'pedidos') }}
                            </template>
                            <template v-slot:item.origem="{ item }">
                                {{ metadataValor(item, 'origem') }}
                            </template>
                            <template v-slot:item.empresa_fantasia="{ item }">
                                <span v-if="!item.empresa_id" class="grey--text">—</span>
                                <span v-else>
                                    {{ item.empresa_fantasia }}
                                    <v-chip v-if="item.qtd_empresas > 1" x-small class="ml-1">
                                        +{{ item.qtd_empresas - 1 }}
                                    </v-chip>
                                </span>
                            </template>
                            <template v-slot:item.empresa_ativa="{ item }">
                                <v-chip v-if="item.empresa_id" :color="item.empresa_ativa ? 'success' : 'grey'" x-small dark>
                                    {{ item.empresa_ativa ? 'Ativa' : 'Inativa' }}
                                </v-chip>
                            </template>
                            <template v-slot:item.total_pago="{ item }">
                                <span :class="Number(item.total_pago) > 0 ? 'green--text font-weight-bold' : 'grey--text'">
                                    R$ {{ formatPrice(item.total_pago) }}
                                </span>
                            </template>
                            <template v-slot:item.created_at="{ item }">
                                {{ formatDate(item.created_at, 'DD/MM/YYYY') }}
                            </template>
                            <template v-slot:item.primeira_empresa_em="{ item }">
                                {{ formatDate(item.primeira_empresa_em, 'DD/MM/YYYY') }}
                            </template>
                            <template v-slot:item.dias_ate_converter="{ item }">
                                {{ item.dias_ate_converter === null ? '—' : `${item.dias_ate_converter}d` }}
                            </template>
                        </v-data-table>
                        <Paginate v-model="paginacao" @input="consultarLista"/>
                    </v-card-text>
                </v-card>

                <!-- Listagem empresa a empresa, com o lead vinculado -->
                <v-card class="mx-auto mt-4" elevation="4" outlined>
                    <div class="text-overline subtitle-2 ma-2" style="text-align: center;">
                        Empresas cadastradas no período
                    </div>

                    <v-card-text>
                        <v-data-table
                            :headers="headersEmpresas"
                            :items="empresas"
                            item-key="empresa_id"
                            :mobileBreakpoint="0"
                            :loading="loadingEmpresas"
                            :items-per-page="-1"
                            hide-default-footer
                            disable-sort
                            dense
                        >
                            <template v-slot:item.empresa_ativa="{ item }">
                                <v-chip :color="item.empresa_ativa ? 'success' : 'grey'" x-small dark>
                                    {{ item.empresa_ativa ? 'Ativa' : 'Inativa' }}
                                </v-chip>
                            </template>
                            <template v-slot:item.empresa_criada_em="{ item }">
                                {{ formatDate(item.empresa_criada_em, 'DD/MM/YYYY') }}
                            </template>
                            <template v-slot:item.contato_id="{ item }">
                                {{ item.contato_id || '—' }}
                            </template>
                            <template v-slot:item.lead_nome="{ item }">
                                <span v-if="!item.contato_id" class="grey--text">Sem lead</span>
                                <span v-else>{{ item.lead_nome }}</span>
                            </template>
                            <template v-slot:item.lead_qtd_empresas="{ item }">
                                <v-chip
                                    v-if="item.lead_qtd_empresas > resumo.limite_empresas_por_lead"
                                    color="red lighten-1"
                                    x-small
                                    dark
                                >
                                    {{ item.lead_qtd_empresas }}
                                </v-chip>
                                <span v-else>{{ item.lead_qtd_empresas || '—' }}</span>
                            </template>
                            <template v-slot:item.lead_score="{ item }">
                                <v-chip v-if="item.contato_id" :color="corScore(item.lead_score)" x-small dark>
                                    {{ item.lead_score }}
                                </v-chip>
                            </template>
                            <template v-slot:item.ramo="{ item }">
                                {{ metadataValor(item, 'ramo', 'lead_metadata') }}
                            </template>
                            <template v-slot:item.faturamento="{ item }">
                                {{ metadataValor(item, 'faturamento', 'lead_metadata') }}
                            </template>
                            <template v-slot:item.tempo_empresa="{ item }">
                                {{ metadataValor(item, 'tempo_empresa', 'lead_metadata') }}
                            </template>
                            <template v-slot:item.pedidos="{ item }">
                                {{ metadataValor(item, 'pedidos', 'lead_metadata') }}
                            </template>
                            <template v-slot:item.origem="{ item }">
                                {{ metadataValor(item, 'origem', 'lead_metadata') }}
                            </template>
                            <template v-slot:item.anuncio="{ item }">
                                <v-tooltip v-if="item.anuncio_nome || item.anuncio_primeiro_nome" bottom max-width="420">
                                    <template v-slot:activator="{ on, attrs }">
                                        <div v-bind="attrs" v-on="on" style="max-width: 240px;">
                                            <div class="text-truncate">
                                                {{ item.anuncio_nome || item.anuncio_primeiro_nome }}
                                            </div>
                                            <div
                                                v-if="anuncioPrimeiroDiferente(item)"
                                                class="text-truncate caption text--secondary"
                                            >
                                                1º: {{ item.anuncio_primeiro_nome }}
                                            </div>
                                        </div>
                                    </template>
                                    <div v-if="item.anuncio_nome">Último anúncio: {{ item.anuncio_nome }}</div>
                                    <div v-if="anuncioPrimeiroDiferente(item) || !item.anuncio_nome">Primeiro anúncio: {{ item.anuncio_primeiro_nome }}</div>
                                </v-tooltip>
                                <span v-else class="grey--text">Sem anúncio</span>
                            </template>
                            <template v-slot:item.lead_criado_em="{ item }">
                                {{ item.lead_criado_em ? formatDate(item.lead_criado_em, 'DD/MM/YYYY') : '—' }}
                            </template>
                            <template v-slot:item.dias_ate_converter="{ item }">
                                {{ item.dias_ate_converter === null ? '—' : `${item.dias_ate_converter}d` }}
                            </template>
                            <template v-slot:item.total_pago="{ item }">
                                <span :class="Number(item.total_pago) > 0 ? 'green--text font-weight-bold' : 'grey--text'">
                                    R$ {{ formatPrice(item.total_pago) }}
                                </span>
                            </template>
                        </v-data-table>
                        <Paginate v-model="paginacaoEmpresas" @input="consultarEmpresas"/>
                    </v-card-text>
                </v-card>

                </v-card-text>
                </v-card>

                </template>
            </v-content>
        </span>
    </BaseContainer>
</template>

<script>
import BaseContainer from '@/components/BaseContainer';
import PeriodoFilter from '@/components/PeriodoFilter';
import Paginate from '@/components/Paginate';
import Card from '@/views/home/Card.vue';

const resumoVazio = {
    leads: 0,
    convertidos: 0,
    pagantes: 0,
    taxa_conversao: 0,
    taxa_pagante: 0,
    taxa_pagante_sobre_convertidos: 0,
    receita: 0,
    receita_media_pagante: 0,
    score_medio_geral: null,
    score_medio_convertidos: null,
    score_medio_pagantes: null,
    score_medio_nao_convertidos: null,
    dias_ate_converter: null,
    leads_outliers: 0,
    outliers_incluidos: false,
    limite_empresas_por_lead: 3,
};

const filtrosVazios = {
    situacao: 'convertidos',
    score_min: null,
    score_max: null,
    ramo: [],
    faturamento: [],
    tempo_empresa: [],
    pedidos: [],
};

export default {
    name: 'RelatorioLeadsConvertidos',

    components: {
        BaseContainer,
        PeriodoFilter,
        Paginate,
        Card,
    },

    data: () => ({
        montado: false,
        periodo: {},
        incluirOutliers: false,
        filtrosAbertos: false,

        loadingDimensao: false,
        loadingLista: false,
        loadingEmpresas: false,

        resumo: { ...resumoVazio },
        porDimensao: [],
        lista: [],
        empresas: [],
        options: {},

        dimensao: 'ramo',
        dimensoes: [
            { text: 'Ramo', value: 'ramo' },
            { text: 'Faixa de score', value: 'score_faixa' },
            { text: 'Faturamento', value: 'faturamento' },
            { text: 'Tempo de empresa', value: 'tempo_empresa' },
            { text: 'Experiência anterior', value: 'experiencia' },
            { text: 'Pedidos/dia', value: 'pedidos' },
            { text: 'Origem', value: 'origem' },
            { text: 'Produto', value: 'produto' },
            { text: 'Impressora', value: 'impressora' },
            { text: 'Plano de interesse', value: 'plano_interesse' },
            { text: 'Anúncio', value: 'anuncio' },
        ],

        filtros: { ...filtrosVazios },

        situacoes: [
            { text: 'Viraram empresa', value: 'convertidos' },
            { text: 'Viraram pagantes', value: 'pagantes' },
            { text: 'Não viraram empresa', value: 'nao_convertidos' },
            { text: 'Todos os leads', value: 'todos' },
        ],

        opcoes: {
            ramo: ['PIZZARIA', 'HAMBURGUERIA', 'RESTAURANTE', 'LANCHONETE', 'ACAITERIA', 'SALGADERIA', 'COMIDA_ORIENTAL', 'MARMITARIA', 'PUB', 'DOCERIA', 'FRANGO_ASSADO', 'PASTELARIA', 'DISTRIBUIDORA_DE_BEBIDAS', 'PADARIA', 'CAFETERIA', 'SORVETERIA', 'OUTROS'],
            // INAUGURACAO no faturamento é a sentinela de quem ainda não abriu — a
            // landingpage esconde a pergunta nesse fluxo.
            faturamento: ['INAUGURACAO', 'ATE_10K', 'DE_10K_A_20K', 'DE_20K_A_40K', 'ACIMA_40K'],
            tempo_empresa: ['INAUGURACAO', 'ATE_6_MESES', 'DE_6M_A_1A', 'DE_1A_A_3A', 'MAIS_3A'],
            pedidos: ['INAUGURACAO', 'DEZ', 'VINTE', 'TRINTA', 'TRINTA_MAIS'],
        },

        headersDimensao: [
            { text: 'Valor', value: 'valor' },
            { text: 'Leads', value: 'leads', align: 'end' },
            { text: 'Viraram empresa', value: 'convertidos', align: 'end' },
            { text: '% conversão', value: 'taxa_conversao', align: 'end' },
            { text: 'Pagantes', value: 'pagantes', align: 'end' },
            { text: '% pagante', value: 'taxa_pagante', align: 'end' },
            { text: 'Receita', value: 'receita', align: 'end' },
            { text: 'Score médio', value: 'score_medio', align: 'end' },
        ],

        headersLista: [
            { text: 'ID', value: 'id', width: '70px' },
            { text: 'Lead', value: 'nome' },
            { text: 'Score', value: 'lead_score', width: '80px' },
            { text: 'Ramo', value: 'ramo', sortable: false },
            { text: 'Faturamento', value: 'faturamento', sortable: false },
            { text: 'Tempo Emp', value: 'tempo_empresa', sortable: false },
            { text: 'Pedidos', value: 'pedidos', sortable: false },
            { text: 'Origem', value: 'origem', sortable: false },
            { text: 'Entrou em', value: 'created_at', width: '110px' },
            { text: 'Empresa', value: 'empresa_fantasia' },
            { text: 'Cadastro em', value: 'primeira_empresa_em', width: '115px' },
            { text: 'Demora', value: 'dias_ate_converter', width: '90px' },
            { text: 'Status', value: 'empresa_ativa', width: '95px', sortable: false },
            { text: 'Pagamentos', value: 'qtd_pagamentos', width: '110px' },
            { text: 'Total pago', value: 'total_pago', width: '120px' },
        ],

        headersEmpresas: [
            { text: 'ID', value: 'empresa_id', width: '70px' },
            { text: 'Empresa', value: 'empresa_fantasia' },
            { text: 'Cadastro em', value: 'empresa_criada_em', width: '115px' },
            { text: 'Status', value: 'empresa_ativa', width: '95px' },
            { text: 'ID Lead', value: 'contato_id', width: '85px' },
            { text: 'Lead', value: 'lead_nome' },
            { text: 'Empresas do lead', value: 'lead_qtd_empresas', width: '90px', align: 'end' },
            { text: 'Score', value: 'lead_score', width: '80px' },
            { text: 'Ramo', value: 'ramo' },
            { text: 'Faturamento', value: 'faturamento' },
            { text: 'Tempo Emp', value: 'tempo_empresa' },
            { text: 'Pedidos', value: 'pedidos' },
            { text: 'Origem', value: 'origem' },
            { text: 'Anúncio', value: 'anuncio' },
            { text: 'Lead entrou em', value: 'lead_criado_em', width: '130px' },
            { text: 'Demora', value: 'dias_ate_converter', width: '90px' },
            { text: 'Pagamentos', value: 'qtd_pagamentos', width: '110px' },
            { text: 'Total pago', value: 'total_pago', width: '120px' },
        ],

        paginacao: {
            current_page: 1,
            last_page: 1,
            total: 0,
        },

        paginacaoEmpresas: {
            current_page: 1,
            last_page: 1,
            total: 0,
        },
    }),

    mounted() {
        this.filtrosAbertos = !this.$vuetify.breakpoint.xsOnly;
        this.montado = true;
        this.consultar();
    },

    computed: {
        // O `perm:` das rotas devolve 403, e o interceptor do axios abre um alert
        // feio. Barrar aqui deixa a tela dizer o que houve sem chamar a API.
        podeConsultar() {
            return this.checkPermission('zapercontrol_relatorios_leads_convertidos_consultar');
        },

        textoAlertaOutliers() {
            const um = this.resumo.leads_outliers === 1;

            if (this.resumo.outliers_incluidos) {
                return um ? 'Está somando nos números abaixo.' : 'Estão somando nos números abaixo.';
            }

            return um ? 'Está fora dos números abaixo.' : 'Estão fora dos números abaixo.';
        },
    },

    watch: {
        // O BaseContainer só busca o usuário (e as permissões) depois que esta
        // tela monta, então no primeiro mount `podeConsultar` é sempre falso e o
        // `consultar()` do mounted desiste. Quando as permissões chegam, consulta.
        podeConsultar(pode) {
            if (pode) this.consultar();
        },

        // O v-data-table sincroniza `options` no próprio mount; sem a guarda a
        // primeira consulta sairia duas vezes.
        options: {
            deep: true,
            handler() {
                if (!this.montado) return;
                this.consultarLista();
            },
        },
    },

    methods: {
        consultar() {
            if (!this.podeConsultar) return;

            this.consultarResumo();
            this.consultarDimensao();
            this.paginacao.current_page = 1;
            this.consultarLista();
            this.paginacaoEmpresas.current_page = 1;
            this.consultarEmpresas();
        },

        paramsPeriodo() {
            const params = new URLSearchParams();

            if (this.periodo.start) {
                params.set('start', this.moment(this.periodo.start).format('YYYY-MM-DD HH:mm:ss'));
            }
            if (this.periodo.end) {
                params.set('end', this.moment(this.periodo.end).format('YYYY-MM-DD HH:mm:ss'));
            }
            if (this.incluirOutliers) {
                params.set('incluir_outliers', '1');
            }

            return params;
        },

        paramsFiltros() {
            const params = this.paramsPeriodo();

            if (this.filtros.score_min !== null && this.filtros.score_min !== '') {
                params.set('score_min', this.filtros.score_min);
            }
            if (this.filtros.score_max !== null && this.filtros.score_max !== '') {
                params.set('score_max', this.filtros.score_max);
            }

            ['ramo', 'faturamento', 'tempo_empresa', 'pedidos'].forEach(campo => {
                (this.filtros[campo] || []).forEach(valor => params.append(`${campo}[]`, valor));
            });

            return params;
        },

        async consultarResumo() {
            if (!this.podeConsultar) return;

            try {
                const resp = await this.$http.get(`relatorios/leads-convertidos/resumo?${this.paramsFiltros()}`);

                if (resp.data.type !== 'success') {
                    throw new Error(resp.data.msg);
                }

                this.resumo = resp.data.data;
            } catch (e) {
                this.resumo = { ...resumoVazio };
                this.notify('Não foi possível carregar o resumo', 'warning');
            }
        },

        async consultarDimensao() {
            if (!this.podeConsultar) return;

            this.loadingDimensao = true;

            try {
                const params = this.paramsFiltros();
                params.set('dimensao', this.dimensao);

                const resp = await this.$http.get(`relatorios/leads-convertidos/por-dimensao?${params}`);

                if (resp.data.type !== 'success') {
                    throw new Error(resp.data.msg);
                }

                this.porDimensao = resp.data.data;
            } catch (e) {
                this.porDimensao = [];
                this.notify('Não foi possível carregar a quebra por característica', 'warning');
            } finally {
                this.loadingDimensao = false;
            }
        },

        async consultarLista() {
            if (!this.podeConsultar) return;

            this.loadingLista = true;

            try {
                const params = this.paramsFiltros();
                params.set('situacao', this.filtros.situacao);
                params.set('page', this.paginacao.current_page);
                params.set('rows', 50);

                if (this.options.sortBy && this.options.sortBy.length) {
                    params.set('sortField', this.options.sortBy[0]);
                    params.set('sortOrder', this.options.sortDesc[0] ? -1 : 1);
                }

                const resp = await this.$http.get(`relatorios/leads-convertidos/lista?${params}`);

                this.lista = resp.data.data;
                this.paginacao = resp.data.meta;
            } catch (e) {
                this.lista = [];
                this.notify('Não foi possível carregar os leads', 'warning');
            } finally {
                this.loadingLista = false;
            }
        },

        async consultarEmpresas() {
            if (!this.podeConsultar) return;

            this.loadingEmpresas = true;

            try {
                // Só o período, e pela data de cadastro da empresa: a tabela é
                // para conferir o vínculo, então filtros de lead não entram.
                const params = this.paramsPeriodo();
                params.set('page', this.paginacaoEmpresas.current_page);
                params.set('rows', 50);

                const resp = await this.$http.get(`relatorios/leads-convertidos/empresas?${params}`);

                this.empresas = resp.data.data;
                this.paginacaoEmpresas = resp.data.meta;
            } catch (e) {
                this.empresas = [];
                this.notify('Não foi possível carregar as empresas', 'warning');
            } finally {
                this.loadingEmpresas = false;
            }
        },

        aplicarFiltros() {
            if (!this.podeConsultar) return;

            this.paginacao.current_page = 1;
            this.consultarResumo();
            this.consultarDimensao();
            this.consultarLista();
        },

        limparFiltros() {
            this.filtros = { ...filtrosVazios, ramo: [], faturamento: [], tempo_empresa: [], pedidos: [] };
            this.aplicarFiltros();
        },

        metadataValor(item, campo, chave = 'metadata') {
            const metadata = item[chave];
            const valor = metadata && metadata[campo];
            return valor || '—';
        },

        // O lead passou por outro anúncio antes do último clique.
        anuncioPrimeiroDiferente(item) {
            return !!item.anuncio_nome
                && !!item.anuncio_primeiro_nome
                && item.anuncio_nome !== item.anuncio_primeiro_nome;
        },

        corScore(score) {
            const s = Number(score || 0);
            if (s >= 71) return 'green';
            if (s >= 31) return 'orange';
            return 'red';
        },

        corTaxa(taxa) {
            const t = Number(taxa || 0);
            const media = Number(this.resumo.taxa_pagante || 0);

            if (!media) return 'grey';
            if (t >= media * 1.5) return 'green';
            if (t >= media * 0.75) return 'blue-grey';
            return 'red lighten-1';
        },
    },
};
</script>
